import { memo, useState } from 'react';

interface ToolbarProps {
  onExport: (format: 'png' | 'png-transparent' | 'pdf') => void;
  onFitView: () => void;
  onResetView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onToggleOrientation: () => void;
  orientation: 'LR' | 'TB';
}

const Toolbar = memo(({
  onExport,
  onFitView,
  onZoomIn,
  onZoomOut,
}: ToolbarProps) => {
  const [showExportMenu, setShowExportMenu] = useState(false);

  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: '#fff',
        padding: 4,
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
        border: '1px solid #e5e7eb',
      }}
    >
      {/* Zoom controls */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <ToolbarButton onClick={onZoomOut} title="Alejar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </ToolbarButton>
        <ToolbarButton onClick={onZoomIn} title="Acercar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </ToolbarButton>
      </div>

      <Divider />

      {/* View controls */}
      <ToolbarButton onClick={onFitView} title="Ajustar vista">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 3h6v6" />
          <path d="M9 21H3v-6" />
          <path d="M21 3l-7 7" />
          <path d="M3 21l7-7" />
        </svg>
      </ToolbarButton>


      {/* Export dropdown */}
      <div style={{ position: 'relative' }}>
        <ToolbarButton
          onClick={() => setShowExportMenu(!showExportMenu)}
          title="Exportar"
          active={showExportMenu}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </ToolbarButton>

        {showExportMenu && (
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 5,
              }}
              onClick={() => setShowExportMenu(false)}
            />
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                background: '#fff',
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '1px solid #e5e7eb',
                minWidth: 160,
                zIndex: 10,
                overflow: 'hidden',
              }}
            >
              <ExportMenuItem
                onClick={() => {
                  onExport('png');
                  setShowExportMenu(false);
                }}
                icon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                }
                label="PNG con fondo"
              />
              <ExportMenuItem
                onClick={() => {
                  onExport('png-transparent');
                  setShowExportMenu(false);
                }}
                icon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeDasharray="4 2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                }
                label="PNG transparente"
              />
              <ExportMenuItem
                onClick={() => {
                  onExport('pdf');
                  setShowExportMenu(false);
                }}
                icon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                }
                label="PDF"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});

// Toolbar button component
interface ToolbarButtonProps {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  active?: boolean;
}

function ToolbarButton({ onClick, title, children, active }: ToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? '#f3f4f6' : 'transparent',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        color: '#4b5563',
        transition: 'background 0.15s ease, color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f3f4f6';
        e.currentTarget.style.color = '#1f2937';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = active ? '#f3f4f6' : 'transparent';
        e.currentTarget.style.color = '#4b5563';
      }}
    >
      {children}
    </button>
  );
}

// Divider component
function Divider() {
  return (
    <div
      style={{
        width: 1,
        height: 20,
        background: '#e5e7eb',
        margin: '0 2px',
      }}
    />
  );
}

// Export menu item
interface ExportMenuItemProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function ExportMenuItem({ onClick, icon, label }: ExportMenuItemProps) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: '#374151',
        fontSize: 13,
        textAlign: 'left',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f3f4f6';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span style={{ color: '#6b7280' }}>{icon}</span>
      {label}
    </button>
  );
}

Toolbar.displayName = 'Toolbar';

export default Toolbar;
